import React from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button visual style variant */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading spinner */
  loading?: boolean;
  /** Button content */
  children: React.ReactNode;
  /** Optional icon to display before text */
  icon?: React.ReactNode;
  /** Full width button */
  fullWidth?: boolean;
}

/**
 * Standardized Button Component
 * Follows UI-UX-PRO-MAX design system for consistency
 * 
 * Usage:
 * <Button variant="primary" size="md">Click me</Button>
 * <Button variant="outline" loading={true}>Loading...</Button>
 * <Button variant="primary" icon={<Save size={18} />}>Save</Button>
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  icon,
  fullWidth = false,
  disabled,
  className = '',
  ...props
}) => {
  // Base classes (always applied)
  const baseClasses = [
    'inline-flex items-center justify-center gap-2',
    'font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'rounded-md',
    fullWidth ? 'w-full' : '',
  ].join(' ');

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  }[size];

  // Variant classes
  const variantClasses = {
    primary: [
      'bg-primary text-white',
      'hover:bg-primary-dark',
      'focus:ring-primary',
      'shadow-sm hover:shadow-md',
    ].join(' '),
    secondary: [
      'bg-slate-600 text-white',
      'hover:bg-slate-700',
      'focus:ring-slate-500',
      'shadow-sm hover:shadow-md',
    ].join(' '),
    outline: [
      'bg-white text-primary',
      'border-2 border-primary',
      'hover:bg-primary hover:text-white',
      'focus:ring-primary',
    ].join(' '),
    ghost: [
      'bg-transparent text-primary',
      'hover:bg-primary/10',
      'focus:ring-primary',
    ].join(' '),
    danger: [
      'bg-red-600 text-white',
      'hover:bg-red-700',
      'focus:ring-red-500',
      'shadow-sm hover:shadow-md',
    ].join(' '),
  }[variant];

  const combinedClasses = [
    baseClasses,
    sizeClasses,
    variantClasses,
    className,
  ].join(' ');

  return (
    <button
      className={combinedClasses}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2 
          size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} 
          className="animate-spin" 
        />
      )}
      {!loading && icon}
      {children}
    </button>
  );
};

/**
 * Icon Button - Square button for icon-only actions
 */
export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children'> {
  icon: React.ReactNode;
  'aria-label': string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  size = 'md',
  className = '',
  ...props
}) => {
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  }[size];

  return (
    <Button
      size={size}
      className={`${sizeClasses} ${className}`}
      {...props}
    >
      {icon}
    </Button>
  );
};

export default Button;
